import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PROTOCOL_TYPE_LABEL, USER_ROLE_LABEL, type ProtocolType, type UserRole } from '@apleona/shared';
import { api } from '../api';
import { PersonForm, emptyPerson, personToForm, type PersonFormValue } from '../components/PersonForm';
import { UserForm, emptyUser, userToForm, type UserFormValue } from '../components/UserForm';
import { F, Modal } from '../components/Modal';
import { Card, Empty, Loading, formatDateTime } from '../components/ui';

// ------------------------------------------------------------------ Personen
export const PersonenPage: React.FC = () => {
  const [q, setQ] = useState('');
  const [form, setForm] = useState<PersonFormValue | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ['personen', q],
    queryFn: () => api<any[]>(`/stammdaten/personen${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  });

  return (
    <div className="grid" style={{ gap: 16 }}>
      <Card title="Suche">
        <div className="toolbar">
          <div className="field grow">
            <label>Name, E-Mail oder Personalnummer</label>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="z. B. Gackowski" />
          </div>
        </div>
      </Card>
      <Card
        title={`${data?.length ?? 0} Personen`}
        bodyless
        actions={
          <button className="btn primary" onClick={() => setForm(emptyPerson())}>+ Neue Person</button>
        }
      >
        {isLoading ? <Loading /> : !data || data.length === 0 ? (
          <Empty text="Keine Personen gefunden" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Name</th><th>E-Mail</th><th>Telefon</th><th>Pers.-Nr.</th><th>Kostenstelle</th><th>Gesellschaft</th><th>Fahrzeug</th><th></th></tr>
              </thead>
              <tbody>
                {data.map((p) => (
                  <tr key={p.id}>
                    <td><b>{[p.firstName, p.lastName].filter(Boolean).join(' ')}</b></td>
                    <td className="small">{p.email ?? '—'}</td>
                    <td className="small">{p.phone ?? '—'}</td>
                    <td className="small mono">{p.personnelNumber ?? '—'}</td>
                    <td className="small">{p.costCenter ?? '—'}</td>
                    <td className="small">{p.company?.name ?? '—'}</td>
                    <td className="small">
                      {p.heldVehicles.length ? p.heldVehicles.map((v: any) => v.plate).join(', ') : '—'}
                    </td>
                    <td className="right">
                      <button className="btn sm" onClick={() => setForm(personToForm(p))}>Bearbeiten</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <PersonForm open={!!form} initial={form ?? emptyPerson()} onClose={() => setForm(null)} />
    </div>
  );
};

// ----------------------------------------------------------------- Verteiler
export const VerteilerPage: React.FC = () => {
  const qc = useQueryClient();
  const [form, setForm] = useState({ email: '', name: '', protocolType: '', mode: 'TO' });
  const [err, setErr] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['verteiler'],
    queryFn: () => api<any[]>('/stammdaten/verteiler'),
  });

  const create = useMutation({
    mutationFn: () =>
      api('/stammdaten/verteiler', {
        method: 'POST',
        body: JSON.stringify({
          email: form.email,
          name: form.name || undefined,
          protocolType: form.protocolType || null,
          mode: form.mode,
        }),
      }),
    onSuccess: () => {
      setForm({ email: '', name: '', protocolType: '', mode: 'TO' });
      setErr(null);
      void qc.invalidateQueries({ queryKey: ['verteiler'] });
    },
    onError: (e: Error) => setErr(e.message),
  });

  const toggle = useMutation({
    mutationFn: (v: { id: string; active: boolean }) =>
      api(`/stammdaten/verteiler/${v.id}`, { method: 'PATCH', body: JSON.stringify({ active: v.active }) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['verteiler'] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/stammdaten/verteiler/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['verteiler'] }),
  });

  return (
    <div className="grid" style={{ gap: 16 }}>
      <div className="alert info">
        Jedes abgeschlossene Protokoll geht automatisch an diese Adressen – zusätzlich immer an den
        Fuhrparkkoordinator, der den Vorgang erfasst hat, sowie an im Tablet ergänzte Empfänger.
      </div>

      <Card title="Neue Regel">
        {err ? <div className="alert error">{err}</div> : null}
        <div className="toolbar">
          <div className="field grow">
            <label>E-Mail</label>
            <input
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="empfaenger@apleona.com"
            />
          </div>
          <div className="field grow">
            <label>Bezeichnung</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="z. B. Leasingpartner"
            />
          </div>
          <div className="field">
            <label>Gilt für</label>
            <select value={form.protocolType} onChange={(e) => setForm({ ...form, protocolType: e.target.value })}>
              <option value="">alle Vorgangsarten</option>
              {Object.entries(PROTOCOL_TYPE_LABEL).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Feld</label>
            <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}>
              <option value="TO">An</option>
              <option value="CC">CC</option>
            </select>
          </div>
          <button
            className="btn primary"
            disabled={!form.email.includes('@') || create.isPending}
            onClick={() => create.mutate()}
          >
            Hinzufügen
          </button>
        </div>
      </Card>

      <Card title={`${data?.length ?? 0} Verteilerregeln`} bodyless>
        {isLoading ? <Loading /> : !data || data.length === 0 ? (
          <Empty icon="✉" text="Noch keine Regeln angelegt" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>E-Mail</th><th>Bezeichnung</th><th>Vorgangsart</th><th>Feld</th><th>Aktiv</th><th></th></tr>
              </thead>
              <tbody>
                {data.map((r) => (
                  <tr key={r.id}>
                    <td><b>{r.email}</b></td>
                    <td className="small">{r.name ?? '—'}</td>
                    <td className="small">
                      {r.protocolType
                        ? PROTOCOL_TYPE_LABEL[r.protocolType as ProtocolType]
                        : <span className="badge blue">alle</span>}
                    </td>
                    <td><span className="badge grey">{r.mode}</span></td>
                    <td>
                      <button
                        className={`badge ${r.active ? 'green' : 'grey'}`}
                        style={{ cursor: 'pointer', border: 'none' }}
                        onClick={() => toggle.mutate({ id: r.id, active: !r.active })}
                      >
                        {r.active ? 'aktiv' : 'inaktiv'}
                      </button>
                    </td>
                    <td className="right">
                      <button className="btn sm" onClick={() => remove.mutate(r.id)}>Löschen</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

// ------------------------------------------------------------------ Benutzer
export const BenutzerPage: React.FC = () => {
  const { data, isLoading } = useQuery({ queryKey: ['benutzer'], queryFn: () => api<any[]>('/benutzer') });
  const [form, setForm] = useState<UserFormValue | null>(null);
  const [reset, setReset] = useState<{ id: string; name: string } | null>(null);

  return (
    <div className="grid" style={{ gap: 16 }}>
    <Card
      title={`${data?.length ?? 0} Benutzer`}
      bodyless
      actions={<button className="btn primary" onClick={() => setForm(emptyUser())}>+ Neuer Benutzer</button>}
    >
      {isLoading ? <Loading /> : !data ? <Empty text="Keine Benutzer" /> : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Name</th><th>E-Mail</th><th>Rolle</th><th>Standort</th><th>Telefon</th><th className="num">Vorgänge</th><th>Letzter Login</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {data.map((u) => (
                <tr key={u.id}>
                  <td><b>{u.firstName} {u.lastName}</b></td>
                  <td className="small">{u.email}</td>
                  <td><span className="badge blue">{USER_ROLE_LABEL[u.role as UserRole]}</span></td>
                  <td className="small">{u.location?.name ?? '—'}</td>
                  <td className="small">{u.phone ?? '—'}</td>
                  <td className="num">{u._count.protocols}</td>
                  <td className="small">{u.lastLoginAt ? formatDateTime(u.lastLoginAt) : 'nie'}</td>
                  <td>
                    <span className={`badge ${u.active ? 'green' : 'grey'}`}>{u.active ? 'aktiv' : 'gesperrt'}</span>
                    {u.mustChangePassword ? <span className="badge warn" style={{ marginLeft: 4 }}>PW ändern</span> : null}
                  </td>
                  <td className="right" style={{ whiteSpace: 'nowrap' }}>
                    <button className="btn sm" onClick={() => setForm(userToForm(u))}>Bearbeiten</button>{' '}
                    <button
                      className="btn sm"
                      onClick={() => setReset({ id: u.id, name: `${u.firstName} ${u.lastName}` })}
                    >
                      Passwort
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>

    <UserForm open={!!form} initial={form ?? emptyUser()} onClose={() => setForm(null)} />
    <PasswordReset target={reset} onClose={() => setReset(null)} />
    </div>
  );
};

// ------------------------------------------------------- Passwort zurücksetzen

const PasswordReset: React.FC<{
  target: { id: string; name: string } | null;
  onClose: () => void;
}> = ({ target, onClose }) => {
  const [pw, setPw] = useState('');
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  React.useEffect(() => {
    if (!target) return;
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    const bytes = crypto.getRandomValues(new Uint32Array(14));
    setPw(Array.from(bytes, (b) => alphabet[b % alphabet.length]).join(''));
    setDone(false);
    setErr(null);
  }, [target]);

  const save = useMutation({
    mutationFn: () =>
      api(`/benutzer/${target!.id}/passwort`, {
        method: 'POST',
        body: JSON.stringify({ password: pw }),
      }),
    onSuccess: () => setDone(true),
    onError: (e: Error) => setErr(e.message),
  });

  if (!target) return null;

  return (
    <Modal
      open
      title={`Passwort zurücksetzen – ${target.name}`}
      onClose={onClose}
      footer={
        done ? (
          <button className="btn primary" onClick={onClose}>Fertig</button>
        ) : (
          <>
            <button className="btn" onClick={onClose}>Abbrechen</button>
            <button className="btn primary" disabled={pw.length < 10 || save.isPending} onClick={() => save.mutate()}>
              Zurücksetzen
            </button>
          </>
        )
      }
    >
      {err ? <div className="alert error">{err}</div> : null}
      {done ? (
        <div className="alert ok">
          Passwort gesetzt. Alle bestehenden Sitzungen wurden beendet – der Benutzer muss das
          Passwort beim nächsten Login ändern.
        </div>
      ) : (
        <div className="alert info">
          Setzt ein neues Startpasswort und beendet alle aktiven Sitzungen dieses Benutzers.
        </div>
      )}
      <F label="Neues Passwort">
        <input
          className="mono"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          readOnly={done}
          onFocus={(e) => e.currentTarget.select()}
          style={{ fontSize: 16, fontWeight: 700 }}
        />
      </F>
    </Modal>
  );
};
